import React, { useEffect } from "react";
import '../assets/styles/Project.scss';

function Project() {
    useEffect(() => {
        if (window.mermaid) {
            window.mermaid.contentLoaded();
        }
    }, []);

    const appArch = `
graph TD
    %% Define Styles
    classDef client fill:#f9f9f9,stroke:#333,stroke-width:2px;
    classDef proxy fill:#f9f,stroke:#333,stroke-width:2px;
    classDef app fill:#bbf,stroke:#333,stroke-width:2px;
    classDef db fill:#fbb,stroke:#333,stroke-width:2px;
    classDef metric fill:#bfb,stroke:#333,stroke-width:2px;

    Client([Navigateurs & Mobiles]):::client -->|HTTPS :443| Caddy[Caddy Server<br/>Reverse Proxy & SSL Auto]:::proxy
    
    subgraph "Docker Swarm / Kubernetes"
        Caddy -->|HTTP :8069<br/>Load Balancing| Odoo1[Odoo Web Node 1]:::app
        Caddy -->|HTTP :8069<br/>Load Balancing| Odoo2[Odoo Web Node N]:::app
        Caddy -->|Longpolling :8072| OdooChat[Odoo Chat/Workers]:::app
        
        Odoo1 -->|Volume Partagé| Filestore[(Volume Docker<br/>web_data)]
        Odoo2 -->|Volume Partagé| Filestore
        OdooChat -->|Volume Partagé| Filestore
        
        Odoo1 -->|TCP 5432| Postgres[(PostgreSQL 15)]:::db
        Odoo2 -->|TCP 5432| Postgres
        OdooChat -->|TCP 5432| Postgres
        
        Postgres -->|Stockage DB| DBVolume[(Volume Docker<br/>db_data)]
        
        %% Observability integration
        PGExporter[Postgres Exporter]:::metric -->|Scrape| Postgres
        CAdvisor[cAdvisor]:::metric -->|Metrics Conteneurs| Odoo1
        Promtail[Promtail]:::metric -->|Logs /var/log| Odoo2
    end
    `;

    const infraArch = `
graph TD
    %% Define Styles
    classDef user fill:#f9f9f9,stroke:#333,stroke-width:2px;
    classDef gitops fill:#ffd700,stroke:#333,stroke-width:2px;
    classDef aws fill:#ff9900,stroke:#333,stroke-width:2px;
    classDef vps fill:#4db8ff,stroke:#333,stroke-width:2px;

    DevOps([Ingénieur DevOps]):::user -->|Déploie via Ansible| Swarm
    DevOps -->|Provisionne via Terraform| AWS
    
    subgraph "Environnement de DEV / Staging (VPS OVH/Hetzner)"
        Swarm[Docker Swarm]:::vps
        Swarm -->|Héberge| OdooDev[Odoo 16]
        Swarm -->|Héberge| PGDev[(PostgreSQL)]
        Swarm -->|Héberge| Monitoring[Prometheus, Grafana, Loki]
    end

    subgraph "Environnement de PRODUCTION (AWS Cloud)"
        AWS[Infrastructure AWS]:::aws
        R53((Route 53<br/>DNS)):::aws -->|Routage| ALB[Application Load Balancer<br/>+ AWS WAF]:::aws
        ALB -->|Trafic Ingress| EKS[Amazon EKS<br/>Kubernetes Cluster]:::aws
        
        EKS -->|Microservices| OdooProd[Odoo 16 Pods]
        
        OdooProd -->|S3 API| S3[(Amazon S3<br/>Backups & Filestore)]:::aws
        OdooProd -->|Connexion Sécurisée| RDS[(Amazon RDS<br/>PostgreSQL Multi-AZ)]:::aws
        OdooProd -->|Récupère| Secrets[AWS Secrets Manager]:::aws
        
        ArgoCD[ArgoCD GitOps]:::gitops -->|Synchronise| EKS
        GitHub[GitHub Actions CI] -->|Pousse Image| ECR[Amazon ECR]:::aws
        ArgoCD -->|Surveille| GitHub
    end
    `;

    return (
    <div className="projects-container" id="projects">
        <h1>Architectures Détaillées SISRH</h1>
        <div className="projects-grid" style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
            <div className="project" style={{ width: '100%' }}>
                <h2>Architecture Applicative (Odoo & Composants)</h2>
                <p>Déploiement haute disponibilité d'Odoo. Intégration d'un reverse proxy Caddy gérant le load balancing et l'auto-renouvellement SSL. Séparation des processus web et chat (longpolling), avec intégration complète de la stack d'observabilité (Promtail, cAdvisor, Postgres Exporter).</p>
                <div style={{ backgroundColor: '#1e1e1e', padding: '20px', borderRadius: '8px', overflowX: 'auto', marginTop: '20px' }}>
                    <pre className="mermaid">
                        {appArch}
                    </pre>
                </div>
            </div>
            
            <div className="project" style={{ width: '100%' }}>
                <h2>Architecture Infrastructure AWS (Production) & VPS (Dev)</h2>
                <p>Infrastructure robuste et scalable définie via Terraform. La production AWS utilise Amazon EKS pour l'orchestration, RDS Multi-AZ pour la base de données sécurisée, S3 pour le stockage, protégée par WAF. Le déploiement est piloté en mode GitOps par ArgoCD.</p>
                <div style={{ backgroundColor: '#1e1e1e', padding: '20px', borderRadius: '8px', overflowX: 'auto', marginTop: '20px' }}>
                    <pre className="mermaid">
                        {infraArch}
                    </pre>
                </div>
            </div>

            <div className="project" style={{ width: '100%' }}>
                <h2>Pipeline CI/CD & Workflow GitOps (Généré par Amazon Q)</h2>
                <p>Ce pipeline illustre l'automatisation complète du build à la production. Il met en avant la séparation des flux pour le développement (Docker Swarm/Portainer) et la production (EKS/ArgoCD), garantissant un cycle de vie logiciel fluide et sécurisé.</p>
                <div style={{ backgroundColor: '#1e1e1e', padding: '20px', borderRadius: '8px', overflowX: 'auto', marginTop: '20px' }}>
                    <pre className="mermaid">
{`graph TB
    subgraph "Developer Workflow"
        DEV[Developer Push Code]
        PR[Pull Request to main]
    end

    subgraph "GitHub Actions CI/CD"
        TRIGGER[Trigger on Push/PR]
        TEST[Run Tests & Linting]
        BUILD[Build Docker Image]
        TAG[Tag Image<br/>dev/prod]
        
        subgraph "Container Registry"
            ECR[Push to AWS ECR]
            DOCKER[Push to Docker Hub]
        end
        
        UPDATE_MANIFEST[Update Helm values.yaml<br/>or K8s manifests]
        COMMIT[Commit & Push to GitOps Repo]
    end

    subgraph "Development Environment - VPS"
        PORTAINER[Portainer Stack]
        SWARM[Docker Swarm]
        ODOO_DEV[Odoo Dev Container]
        PG_DEV[PostgreSQL Dev]
        
        PORTAINER -->|Deploy Stack| SWARM
        SWARM -->|Orchestrate| ODOO_DEV
        SWARM -->|Orchestrate| PG_DEV
    end

    subgraph "Production Environment - AWS"
        subgraph "EKS Cluster"
            ARGOCD[ArgoCD]
            ARGOCD_SYNC[Auto Sync<br/>Every 3min]
            
            subgraph "Odoo Namespace"
                INIT[Init Container<br/>-i base / -u all]
                ODOO_POD[Odoo 16 Pod]
                INGRESS[Nginx Ingress]
            end
            
            RDS[(RDS PostgreSQL)]
        end
        
        ELB[AWS Load Balancer]
    end

    subgraph "GitOps Repository"
        HELM_REPO[Helm Charts<br/>infra/helm/odoo-16]
        VALUES_DEV[values-dev.yaml]
        VALUES_PROD[values-prod.yaml]
    end

    subgraph "DNS"
        DNS_DEV[dev.erptechnova.sis-rh.com]
        DNS_PROD[prod.erptechnova.sis-rh.com]
    end

    %% Workflow connections
    DEV --> PR
    PR --> TRIGGER
    TRIGGER --> TEST
    TEST -->|Success| BUILD
    BUILD --> TAG
    
    TAG -->|Dev Branch| DOCKER
    TAG -->|Main Branch| ECR
    
    DOCKER -->|Pull Image| PORTAINER
    ECR --> UPDATE_MANIFEST
    UPDATE_MANIFEST --> COMMIT
    COMMIT --> HELM_REPO
    
    HELM_REPO --> VALUES_DEV
    HELM_REPO --> VALUES_PROD
    
    VALUES_PROD --> ARGOCD
    ARGOCD --> ARGOCD_SYNC
    ARGOCD_SYNC -->|Detect Changes| ARGOCD
    ARGOCD -->|Deploy| INIT
    INIT -->|Initialize DB| ODOO_POD
    ODOO_POD --> INGRESS
    INGRESS --> ELB
    ODOO_POD -.->|Connect| RDS
    
    ELB --> DNS_PROD
    PORTAINER --> DNS_DEV
    
    %% Styling
    classDef aws fill:#FF9900,stroke:#232F3E,stroke-width:2px,color:#fff
    classDef github fill:#2088FF,stroke:#0366d6,stroke-width:2px,color:#fff
    classDef gitops fill:#00D4AA,stroke:#008B6F,stroke-width:2px,color:#fff
    classDef dev fill:#13BEF9,stroke:#0E8FC4,stroke-width:2px,color:#fff
    
    class ECR,ELB,RDS,EKS aws
    class TRIGGER,TEST,BUILD,TAG,UPDATE_MANIFEST,COMMIT github
    class ARGOCD,ARGOCD_SYNC,HELM_REPO,VALUES_DEV,VALUES_PROD gitops
    class PORTAINER,SWARM,ODOO_DEV,PG_DEV dev`}
                    </pre>
                </div>
            </div>
        </div>
    </div>
    );
}

export default Project;
